import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  AppBar,
  Toolbar,
  Box,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn,
  ZoomOut,
  Rotate90DegreesCw as RotateIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { apiCall } from '../../services/ApiCall';
import config from "../../config/config.json";
import { useLocation } from 'react-router-dom';
import { heicTo } from 'heic-to';

const ImageViewer = ({
  image,
  alt = "Image preview",
  previewStyle = {},
  className = "",
  reportId,
  initialScale = 1,
  maxScale = 3,
  minScale = 0.5,
  uploadedImages,
  setUploadedImages,
}) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(initialScale);
  const [savingRotation, setSavingRotation] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setRotation(0);
    setScale(initialScale);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, maxScale));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, minScale));
  };

  const convertHeicToImage = async (url) => {
    try {
      const response = await fetch(url); 
      const blob = await response.blob();
      const convertedBlob = await heicTo({
        blob,
        type: "image/jpeg"
      });
      const objectURL = URL.createObjectURL(convertedBlob); 
      return objectURL;
    } catch (error) {
      console.error('Error converting HEIC file:', error);
      return null; 
    }
  };
  
  const convertHeicFields = async (data) => {
    const convertedData = { ...data };
  
    for (let i = 1; i <= 24; i++) {
      const pField = `p${i}`;
      if (convertedData[pField] && (convertedData[pField].endsWith('.heic') || convertedData[pField].endsWith('.HEIC'))) {
        const convertedImageUrl = await convertHeicToImage(convertedData[pField]);
        if (convertedImageUrl) {
          convertedData[pField] = convertedImageUrl;
        }
      }
    }
  
    return convertedData; 
  };

  // Create rotated image file using canvas from proxy API (to avoid CORS)
  const createRotatedImageFile = async (imageUrl, rotation) => {
    return new Promise(async (resolve, reject) => {
      try {
        
        // Use existing proxy API to get image as base64 (avoids CORS)
        const { isSuccess, data } = await apiCall(
          `${config.POST_LOGIN_API_URL}getProxyImageUrl`,
          {},
          { url: encodeURIComponent(imageUrl) },
          "GET"
        );

        if (!isSuccess || !data || !data.data) {
          throw new Error('Failed to get image from proxy API');
        }

        const proxyUrl = `data:image/jpeg;base64,${data.data}`;
        
        const img = new Image();
        
        img.onload = () => {
          
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas dimensions based on rotation
          if (rotation === 90 || rotation === 270) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }
          
          
          // Apply rotation
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);
          
          // Convert canvas to blob (file) - this works since we're using base64 data URL
          canvas.toBlob((blob) => {
            if (blob) {
              const timestamp = new Date().getTime();
              const fileName = `${timestamp}_rotated_${reportId}.jpg`;
              const file = new File([blob], fileName, { type: 'image/jpeg' });
              resolve(file);
            } else {
              reject(new Error('Failed to create image blob'));
            }
          }, 'image/jpeg', 0.9);
        };
        
        img.onerror = (error) => {
          console.error('❌ Image load error:', error);
          reject(error);
        };
        
        // Load the image from the base64 data URL (no CORS issues)
        img.src = proxyUrl;
        
      } catch (error) {
        console.error('❌ Error in createRotatedImageFile:', error);
        reject(error);
      }
    });
  };

  // Upload file using existing uploadFile API (same as PictureForm.js)
  const uploadRotatedFile = async (file) => {
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);
    formData.append("filePath", "msi/");


    const requestOptions = {
      method: "POST",
      body: formData,
    };

    const response = await fetch(`${config.AUTH_API_URL}uploadFile`, requestOptions);

    if (!response.ok) {
      console.error('Upload failed with status:', response.status);
      throw new Error("Failed to upload file");
    }

    const result = await response.json();
    return result.url;
  };

  // Update picture record using existing uploadPicture API (same as PictureForm.js)
  const updatePictureRecord = async (pictureUrl) => {

    const requestData = {
      requestId: Number(location.state?.requestId),
      pictureId: reportId,
      pictureUrl: pictureUrl,
      setNewTime: false
    };

    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}uploadPicture`,
      {},
      requestData,
      "POST"
    );

    if (!isSuccess) {
      console.error('uploadPicture API failed');
      throw new Error('Failed to update picture record');
    }

    return data;
  };

  const saveRotatedImage = async () => {
    
    try {
      setSavingRotation(true);
      
      if (rotation === 0) {
        setSavingRotation(false);
        return; // No rotation to save
      }

      if (!image) {
        throw new Error('No image found to rotate');
      }

      // Step 1: Create rotated image file
      const rotatedFile = await createRotatedImageFile(image, rotation);
      
      // Step 2: Upload file using existing upload logic
      const newImageUrl = await uploadRotatedFile(rotatedFile);
      
      // Step 3: Update picture record using existing uploadPicture logic
      const data = await updatePictureRecord(newImageUrl);

      // Step 4: Update local state with new image data
      const tmpData = data.data[0];
      const convertedData = await convertHeicFields(tmpData);
      if (setUploadedImages) {
        setUploadedImages(convertedData);
      }
      
      // Reset rotation state since image is now saved with rotation
      setRotation(0);
      
      handleClose(); // Close the viewer after saving
      
    } catch (error) {
      console.error('❌ Error saving rotated image:', error);
      alert('Failed to save rotated image. Please try again.');
    } finally {
      setSavingRotation(false);
    }
  };

  return (
    <>
      <img
        src={image}
        alt={alt}
        reportId={reportId}
        className={className}
        style={{ cursor: 'pointer', ...previewStyle }}
        onClick={handleOpen}
      />

      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          },
        }}
      >

        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            marginTop: '64px', // Height of AppBar
            overflow: 'hidden',
          }}
        >
          <img
            src={image}
            alt={alt}
            reportId={reportId}
            style={{
              maxHeight: 'calc(100vh - 64px)',
              maxWidth: '100%',
              objectFit: 'contain',
              transform: `rotate(${rotation}deg) scale(${scale})`,
              transition: 'transform 0.3s ease',
            }}
          />
        </DialogContent>

        <AppBar position="fixed" color="default" elevation={0} sx={{ top: 'auto', bottom: 0 }}>
          <Toolbar>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Zoom Out">
                <IconButton 
                  onClick={handleZoomOut}
                  color="primary"
                >
                  <ZoomOut />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom In">
                <IconButton 
                  onClick={handleZoomIn}
                  color="primary"
                >
                  <ZoomIn />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate">
                <IconButton 
                  onClick={handleRotate}
                  color="primary"
                >
                  <RotateIcon />
                </IconButton>
              </Tooltip>
              {rotation !== 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveRotatedImage}
                  disabled={savingRotation}
                  startIcon={savingRotation ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  sx={{ ml: 2 }}
                >
                  {savingRotation ? 'Saving...' : 'Save Image'}
                </Button>
              )}
              <Tooltip title="Close">
                <IconButton
                  edge="end"
                  color="primary"
                  onClick={handleClose}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      </Dialog>
    </>
  );
};

export default ImageViewer;